import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  TextField,
  IconButton,
  Alert,
} from '@mui/material'
import { useState, useEffect } from 'react'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import type { WhitelistEntry } from '@/types'
import { useInvoiceAnalyser } from '@/hooks/useInvoiceAnalyser'
import { useT } from '@/i18n/useT'

interface SupplierWhitelistSectionProps {
  open?: boolean
}

export default function SupplierWhitelistSection({ open }: SupplierWhitelistSectionProps) {
  const [suppliers, setSuppliers] = useState<WhitelistEntry[]>([])
  const { t } = useT()
  const [loading, setLoading] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const [newSupplier, setNewSupplier] = useState<WhitelistEntry>({
    id: '',
    names: [''],
    normalizedName: '',
    ustId: ''
  })

  const {
    getSupplierWhitelist,
    addSupplierToWhitelist,
    deleteSupplierFromWhitelist,
  } = useInvoiceAnalyser()

  // Load whitelist when dialog opens
  useEffect(() => {
    if (open) {
      loadWhitelist()
    }
  }, [open])

  const loadWhitelist = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getSupplierWhitelist()
      setSuppliers(data.suppliers)
    } catch (err) {
      console.error('Failed to load supplier whitelist:', err)
      setError(t('Could not load the whitelist'))
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = async () => {
    if (!newSupplier.id || !newSupplier.normalizedName || !newSupplier.ustId) {
      setError(t('Please fill in all required fields'))
      return
    }

    const names = newSupplier.names[0]
      ? newSupplier.names[0].split(',').map(s => s.trim()).filter(Boolean)
      : [newSupplier.normalizedName]

    const supplierToAdd: WhitelistEntry = {
      ...newSupplier,
      names
    }

    try {
      await addSupplierToWhitelist(supplierToAdd)
      await loadWhitelist()
      setShowAdd(false)
      setNewSupplier({ id: '', names: [''], normalizedName: '', ustId: '' })
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error('Failed to add supplier:', err)
      setError(err instanceof Error ? err.message : t('Could not add the supplier'))
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteSupplierFromWhitelist(id)
      await loadWhitelist()
    } catch (err) {
      console.error('Failed to delete supplier:', err)
      setError(t('Could not delete the supplier'))
    }
  }

  const generateId = (name: string) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">{t('Supplier whitelist')}</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setShowAdd(!showAdd)}
        >
          {t('Add supplier')}
        </Button>
      </Stack>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('Supplier added')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {showAdd && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
            {t('Add a new supplier to the whitelist')}
          </Typography>
          <Stack spacing={1}>
            <TextField
              size="small"
              label={t('ID')}
              value={newSupplier.id}
              onChange={(e) => setNewSupplier({ ...newSupplier, id: e.target.value })}
              placeholder={`${t('e.g.')} acme-corp`}
              onBlur={() => {
                if (!newSupplier.id && newSupplier.normalizedName) {
                  setNewSupplier({ ...newSupplier, id: generateId(newSupplier.normalizedName) })
                }
              }}
            />
            <TextField
              size="small"
              label={t('Normalised name')}
              value={newSupplier.normalizedName}
              onChange={(e) => setNewSupplier({ ...newSupplier, normalizedName: e.target.value })}
              placeholder={`${t('e.g.')} ACME Corporation`}
            />
            <TextField
              size="small"
              label={t('VAT ID')}
              value={newSupplier.ustId}
              onChange={(e) => setNewSupplier({ ...newSupplier, ustId: e.target.value })}
              placeholder={`${t('e.g.')} DE123456789`}
            />
            <TextField
              size="small"
              label={t('Name variations (comma-separated)')}
              value={newSupplier.names[0] || ''}
              onChange={(e) => setNewSupplier({
                ...newSupplier,
                names: [e.target.value]
              })}
              placeholder={`${t('e.g.')} ACME, Acme Corp, ACME GmbH`}
              helperText={t('Different ways this supplier might appear on invoices')}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleAdd}
                disabled={!newSupplier.id || !newSupplier.normalizedName || !newSupplier.ustId}
              >
                {t('Add to whitelist')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setShowAdd(false)
                  setNewSupplier({ id: '', names: [''], normalizedName: '', ustId: '' })
                }}
              >
                {t('Cancel')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {loading ? (
        <Typography variant="body2" color="textSecondary">
          {t('Loading whitelist …')}
        </Typography>
      ) : suppliers.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          {t('No suppliers in the whitelist yet — add the first one above.')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {suppliers.map((supplier) => (
            <Paper key={supplier.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {supplier.normalizedName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t('ID')}: {supplier.id} • {t('VAT ID')}: {supplier.ustId || '—'}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="textSecondary" sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block'
                    }}>
                      {t('Variations')}: {supplier.names.join(', ')}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(supplier.id)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
        {t('Suppliers in the whitelist')}: {suppliers.length}
      </Typography>
    </Box>
  )
}
